import { useState, useCallback, useMemo } from 'react'
import { Button, GenericDialog } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import {
  ImportedGood,
  BulkUpdateImportedGoodsUpdates,
  useBulkUpdateImportedGoods
} from '@/features/cbam-reporting/hooks/importedGoods'
import { Importer } from '@/features/importers/hooks/importers'
import toast from 'react-hot-toast'
import BulkUpdateImporterForm from '../components/bulkUpdateImporterForm/BulkUpdateImporterForm'
import {
  BulkUpdateImporterFormData,
  getInitialFormData,
  validateBulkUpdateImporterForm,
  EMPTY_VALUE
} from '../components/bulkUpdateImporterForm/BulkUpdateImporterForm.schema'

export default function BulkUpdateImporterDialog({
  isOpen,
  close,
  selectedGoods,
  organizationId,
  cbamReportId,
  importers,
  onSuccess
}: {
  isOpen: boolean
  close: () => void
  selectedGoods: ImportedGood[]
  organizationId: string
  cbamReportId: string
  importers: Importer[]
  onSuccess: () => void
}) {
  const { t } = useI18n()
  const { mutateAsync: bulkUpdate, isPending } = useBulkUpdateImportedGoods(organizationId, cbamReportId)
  const [formData, setFormData] = useState<BulkUpdateImporterFormData>(getInitialFormData())

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData())
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    close()
  }, [close, resetForm])

  const buildUpdates = useCallback((): BulkUpdateImportedGoodsUpdates => {
    const updates: BulkUpdateImportedGoodsUpdates = {}
    if (formData.importerId) {
      updates.importerId = formData.importerId === EMPTY_VALUE ? null : formData.importerId
    }
    return updates
  }, [formData])

  const isFormValid = useMemo(() => validateBulkUpdateImporterForm(formData).success, [formData])

  const handleSubmit = useCallback(async () => {
    try {
      const updates = buildUpdates()
      await bulkUpdate({
        ids: selectedGoods.map(good => good.id),
        updates
      })
      toast.success(
        t('cbamReporting:bulkUpdate.notifications.success', { count: selectedGoods.length })
      )
      onSuccess()
      handleClose()
    } catch (error) {
      console.error('Bulk update importer failed:', error)
      toast.error(t('cbamReporting:bulkUpdate.notifications.error'))
    }
  }, [bulkUpdate, selectedGoods, buildUpdates, t, onSuccess, handleClose])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={handleClose}
      title={t('cbamReporting:bulkUpdate.importerDialog.title')}
      subtitle={t('cbamReporting:bulkUpdate.importerDialog.subtitle', {
        count: selectedGoods.length
      })}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">{t('cbamReporting:bulkUpdate.description')}</p>
        <BulkUpdateImporterForm
          data={formData}
          onChange={setFormData}
          importers={importers}
          selectedGoods={selectedGoods}
        />
        <footer className="flex justify-end gap-2">
          <Button variant="neutral" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid || isPending} isLoading={isPending}>
            {t('cbamReporting:bulkUpdate.actions.update', { count: selectedGoods.length })}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
